import React from 'react';
import { Dispatch } from 'redux';
import {
    ArrowLeft,
    Calendar,
    Clock,
    Settings,
    MoreVertical,
    Wrench,
    Activity,
    DollarSign,
    FileText,
} from 'lucide-react';

import axios from 'helpers/axios';
import config from 'helpers/config';
import User from 'models/UserModel';
import UserInspections from './UserInspections';
import Inspection from 'models/InspectionModel';
import history from 'helpers/history';
import UserRepairOrders from './UserRepairOrders';
import UserLoginsList from './UserLoginsList';
import moment from 'moment';
import dateFormats from 'helpers/dateFormats';

interface Props {
    dispatch: Dispatch;
}

interface State {
    isEditMode: boolean;
    activeTab: number;

    isLoading: boolean;
    user?: User;
    userInspections: Inspection[];
    repairOrdersCount?: number;
    paymentsCount?: number;
    loginsCount?: number;
    filesCount?: number;
}

class UserDetailsPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            activeTab: 0,
            isEditMode: false,

            isLoading: true,
            userInspections: [],
        };
    }

    componentDidMount() {
        this.getUser();
        this.getUserInspections();
    }

    getUserInspections() {
        // @ts-ignore
        const { userId } = this.props.match.params;

        axios.get(`${config.apiUrl}/inspection/getAllByUser/${userId}`).then((res) => {
            this.setState({ userInspections: res.data });
        });
    }

    getUser() {
        // @ts-ignore
        const { userId } = this.props.match.params;

        axios.get(`${config.apiUrl}/user/${userId}?withLastLogin=1`).then((res) => {
            this.setState({ user: res.data, isLoading: false });
        });
    }

    getTabs() {
        return [
            {
                title: 'Repair Orders',
                count: this.state.repairOrdersCount || 0,
                icon: <Wrench className="w-4 h-4" />,
                component: (
                    <UserRepairOrders
                        // @ts-ignore
                        userId={this.props.match.params.userId}
                        onRepairOrdersCountChange={(count) => this.setState({ repairOrdersCount: count })}
                    />
                ),
            },
            {
                title: 'Login Sessions',
                count: this.state.loginsCount || 0,
                icon: <Activity className="w-4 h-4" />,
                component: (
                    <div>
                        {this.state.user && (
                            <UserLoginsList
                                userId={this.state.user.id}
                                onLoginsCountChange={(count) => this.setState({ loginsCount: count })}
                            />
                        )}
                    </div>
                ),
            },
            {
                title: 'Payments',
                count: this.state.paymentsCount || 0,
                icon: <DollarSign className="w-4 h-4" />,
                component: <div></div>,
            },
            {
                title: 'Files',
                count: this.state.filesCount || 0,
                icon: <FileText className="w-4 h-4" />,
                component: <div></div>,
            },
        ];
    }
    render() {
        const { activeTab, isLoading, user } = this.state;
        const tabs = this.getTabs();

        if (isLoading) {
            return (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="text-gray-500">Loading...</div>
                </div>
            );
        }

        if (!user) {
            return (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="text-gray-500">User not found</div>
                </div>
            );
        }

        // Get initials for avatar
        const initials = `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();

        return (
            <div className="min-h-screen bg-gray-50 pb-96">
                {/* Header */}
                <div className="bg-white border-b border-gray-200">
                    <div className="px-6 py-4">
                        {/* Back button */}
                        <button
                            onClick={() => history.goBack()}
                            className="flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            <span>Back</span>
                        </button>

                        {/* User profile header */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                {/* Avatar */}
                                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                                    <span className="text-xl font-bold text-white">{initials}</span>
                                </div>

                                {/* User info */}
                                <div>
                                    <div className="flex items-center space-x-3">
                                        <h1 className="text-2xl font-bold text-gray-900">
                                            {user.firstName} {user.lastName}
                                        </h1>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                                            Active
                                        </span>
                                    </div>
                                    <p className="text-gray-600 mt-1">{user.email || 'admin@repairshop.com'}</p>
                                </div>
                            </div>

                            {/* Action button */}
                            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                                <MoreVertical className="w-5 h-5" />
                            </button>
                        </div>

                        {/* User meta info */}
                        <div className="flex items-center space-x-6 mt-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                                <Settings className="w-4 h-4" />
                                <span>Administrator</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4" />
                                <span>
                                    Joined:{' '}
                                    <span className="font-semibold">
                                        {moment(user.createdAt).format(dateFormats.USA_DATE_TIME_LONG)}
                                    </span>
                                </span>
                            </div>
                            {user.lastLogin && (
                                <div className="flex items-center space-x-2">
                                    <Clock className="w-4 h-4" />
                                    <span>
                                        Last login:{' '}
                                        <span className="font-semibold">
                                            {moment(user.lastLogin?.createdAt).format(dateFormats.USA_DATE_TIME_LONG)}
                                        </span>
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="px-6">
                        <div className="flex space-x-8 border-b border-gray-200">
                            {tabs.map((tab, index) => {
                                const isActive = activeTab === index;
                                return (
                                    <button
                                        key={index}
                                        onClick={() => this.setState({ activeTab: index })}
                                        className={`flex items-center space-x-2 py-4 border-b-2 font-semibold text-sm transition-colors ${
                                            isActive
                                                ? 'border-blue-600 text-blue-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                    >
                                        {tab.icon}
                                        <span>{tab.title}</span>
                                        <span
                                            className={`px-2 py-1 text-sm rounded-full ${
                                                isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                                            }`}
                                        >
                                            {tab.count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">{tabs[activeTab].component}</div>
            </div>
        );
    }
}

export default UserDetailsPage;
